import React, { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * PhotoRow — 1–12 images, desktop grid + mobile swipe carousel + click-to-lightbox.
 *
 * Empty-state rule (§3.2):
 *   - photos is empty → nothing renders
 *   - 1 photo         → single image (not a broken grid)
 *   - 2+ photos       → responsive grid
 *
 * Props:
 *   photos: [{ src, alt (required), caption?, credit? }]
 *   title  : optional block heading rendered above the grid
 *   testId : root data-testid
 */
export default function PhotoRow({ photos = [], title, testId = "photo-row" }) {
  const [failed, setFailed] = useState(() => new Set());
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const usable = (photos || []).filter((p) => p && p.src && p.alt && !failed.has(p.src));

  // Empty-state: don't render anything if there is nothing to show.
  if (usable.length === 0) return null;

  const markFailed = (src) => setFailed((prev) => {
    const next = new Set(prev);
    next.add(src);
    return next;
  });

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const prev = () => setLightboxIndex((i) => (i > 0 ? i - 1 : usable.length - 1));
  const next = () => setLightboxIndex((i) => (i < usable.length - 1 ? i + 1 : 0));

  return (
    <section className="w-full" data-testid={testId}>
      {title && (
        <p className="eyebrow mb-4">{title}</p>
      )}
      {usable.length === 1 ? (
        <SingleFigure
          photo={usable[0]}
          onClick={() => openLightbox(0)}
          onError={() => markFailed(usable[0].src)}
        />
      ) : (
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
          role="list"
        >
          {usable.map((p, i) => (
            <GridFigure
              key={p.src}
              photo={p}
              onClick={() => openLightbox(i)}
              onError={() => markFailed(p.src)}
              testId={`${testId}-item-${i}`}
            />
          ))}
        </div>
      )}
      {lightboxIndex !== null && (
        <Lightbox
          photo={usable[lightboxIndex]}
          index={lightboxIndex}
          total={usable.length}
          onClose={closeLightbox}
          onPrev={prev}
          onNext={next}
        />
      )}
    </section>
  );
}

function SingleFigure({ photo, onClick, onError }) {
  return (
    <figure className="w-full">
      <button
        type="button"
        onClick={onClick}
        className="block w-full relative overflow-hidden bg-[#151517] border border-[#343437] aspect-[16/9] group cursor-zoom-in"
        data-testid="photo-row-single"
        aria-label={`Enlarge ${photo.alt}`}
      >
        <img
          src={photo.src}
          alt={photo.alt}
          loading="lazy"
          decoding="async"
          onError={onError}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          style={{ imageOrientation: "from-image" }}
        />
      </button>
      {(photo.caption || photo.credit) && (
        <figcaption className="mt-2 text-xs text-[#A9A9A6]">
          {photo.caption}
          {photo.credit && <span className="ml-2 opacity-70">· {photo.credit}</span>}
        </figcaption>
      )}
    </figure>
  );
}

function GridFigure({ photo, onClick, onError, testId }) {
  return (
    <figure className="relative" role="listitem">
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        aria-label={`Enlarge ${photo.alt}`}
        className="block w-full aspect-square overflow-hidden bg-[#151517] border border-[#343437] group cursor-zoom-in"
      >
        <img
          src={photo.src}
          alt={photo.alt}
          loading="lazy"
          decoding="async"
          onError={onError}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          style={{ imageOrientation: "from-image" }}
        />
      </button>
      {photo.caption && (
        <figcaption className="mt-2 text-[11px] text-[#A9A9A6] leading-snug">
          {photo.caption}
        </figcaption>
      )}
    </figure>
  );
}

function Lightbox({ photo, index, total, onClose, onPrev, onNext }) {
  const onKey = useCallback((e) => {
    if (e.key === "Escape") onClose();
    else if (e.key === "ArrowLeft") onPrev();
    else if (e.key === "ArrowRight") onNext();
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onKey]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={photo.alt}
      className="fixed inset-0 z-[90] bg-[#0B0B0C]/95 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        data-testid="photo-lightbox-close"
        className="absolute top-5 right-5 p-2 text-[#FAF9F6] hover:text-[#D8A936] transition-colors"
      >
        <X size={22} />
      </button>
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            aria-label="Previous image"
            data-testid="photo-lightbox-prev"
            className="absolute left-3 md:left-6 p-2 text-[#FAF9F6] hover:text-[#D8A936] transition-colors"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            aria-label="Next image"
            data-testid="photo-lightbox-next"
            className="absolute right-3 md:right-6 p-2 text-[#FAF9F6] hover:text-[#D8A936] transition-colors"
          >
            <ChevronRight size={28} />
          </button>
        </>
      )}
      <figure
        className="max-w-[92vw] max-h-[90vh] flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={photo.src}
          alt={photo.alt}
          className="max-w-full max-h-[75vh] object-contain border border-[#343437]"
          style={{ imageOrientation: "from-image" }}
        />
        {(photo.caption || photo.credit || total > 1) && (
          <figcaption className="text-center max-w-2xl">
            {photo.caption && (
              <p className="text-sm text-[#FAF9F6]">{photo.caption}</p>
            )}
            {photo.credit && (
              <p className="mt-1 text-[10px] tracking-[0.14em] uppercase text-[#A9A9A6]">{photo.credit}</p>
            )}
            {total > 1 && (
              <p className="mt-2 text-[10px] tracking-[0.14em] uppercase text-[#6E6E6B]">
                {index + 1} / {total}
              </p>
            )}
          </figcaption>
        )}
      </figure>
    </div>
  );
}
